/**
 * CompanyPanel Component (New Design System)
 *
 * Redesigned Company management panel using the new design system components.
 */

import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useOrgDetailLink } from '../../useOrgDetailLink';
import { Grid, FormControlLabel, Checkbox, List, ListItem, ListItemText, Typography, Chip, Stack } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';

// Design System Components
import {
    colors,
    EntityPageLayout,
    EntityPageHeader,
    InfoCard,
    ActivityCard,
    DraftTextField,
    DraftSelectField,
    ReadOnlyField,
    Button,
    ConfirmDialog,
} from '@components/design-system';

// Existing utilities and hooks
import { Operation } from '@/types/general';
import { useBcCustomersQuery, useCompanyMutation, useCompanyQuery } from '@features/redux/apis/org';
import { useAuditHistoryQuery } from '@features/redux/apis/audit';
import { Query } from '@features/redux/apis/utils';
import { Validate } from '@utils/validate';
import { DEFAULT_BILLING_OPTIONS } from '@consts/company';
import { navigateToActivity } from '@utils/activityLinks';
import { CompanyEffects } from './CompanyEffects';
import { ConfirmModal } from '@components/dialogs/ConfirmModal';
import { AppRoutes } from '@consts/routes';
import { BannerEditor } from '@components/banners';
import { BannerScope } from '@api/banners';

import type { ManageProps } from '../Panel';
import type { RootState } from '@features/redux/store';

// Draft state setup
const useCompanies = Query.withDraft(useCompanyQuery, {
    Reseller: false,
    CompanyName: '',
    Inactive: false,
    CustomerID: null,
    SalesRepTrackingEnabled: true,
    DeviceIdEnabled: true,
    ...DEFAULT_BILLING_OPTIONS,
});

export const CompanyPanel: React.FC<ManageProps> = ({ operation, id: companyId, onSuccess }) => {
    const navigate = useNavigate();
    const detailLink = useOrgDetailLink();
    const { role } = useSelector(({ auth }: RootState) => auth.Roles);
    const auditPk = companyId ? `Company#${companyId}` : null;

    const [confirmOpen, setConfirmOpen] = useState(false);

    // Fetch BC customers
    const { data: customers, isFetching: customersLoading } = useBcCustomersQuery({});
    const customerMap = useMemo(
        () =>
            customers?.reduce((acc, { label, value }) => {
                acc[value] ??= label;
                return acc;
            }, {} as Record<string, string>) ?? {},
        [customers]
    );

    const customerOptions = useMemo(() => customers?.map(({ label, value }) => ({ label, value })) ?? [], [customers]);

    // Fetch company data
    const {
        draft,
        data,
        isFetching: queryLoading,
    } = useCompanies(
        { companyId: +companyId! },
        { skip: operation === Operation.Create, refetchOnMountOrArgChange: true }
    );
    const [draftState, setDraftState] = draft;

    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

    // Mutation
    const [mutate, { isLoading: mutationLoading }] = useCompanyMutation({
        fixedCacheKey: operation === Operation.Edit ? 'CompanyMutation' : 'CompanyCreation',
    });

    // Audit history
    const { data: auditData = [] } = useAuditHistoryQuery(
        { entityPk: auditPk! },
        { skip: !auditPk || operation === Operation.Create }
    );

    const handleArchiveToggle = async () => {
        await mutate({
            method: 'PATCH',
            company: { ...draftState, Inactive: !draftState.Inactive },
        });
        setDraftState((prev) => ({ ...prev, Inactive: !prev.Inactive }));
        setArchiveDialogOpen(false);
    };

    const handleSubmit = async () => {
        const result = await mutate({
            method: operation === Operation.Create ? 'POST' : 'PATCH',
            company: draftState,
        });

        // Navigate to edit page after successful creation
        if (operation === Operation.Create && 'data' in result && result.data?.CompanyID) {
            navigate(detailLink('company', result.data.CompanyID));
        } else {
            onSuccess?.();
        }
    };

    const isLoading = queryLoading || customersLoading || mutationLoading;
    const reassigningCustomer = operation === Operation.Edit && draftState.CustomerID !== data?.CustomerID;
    const isValid = Validate.Org.company(draftState);
    const canSave = role.isAdmin && role.stores.edit;


    const pageTitle = operation === Operation.Create ? 'Create New Company' : data?.CompanyName || 'Company';

    return (
        <>
            <CompanyEffects />

            <EntityPageLayout loading={isLoading && !data}>
                {/* Header */}
                <EntityPageHeader
                    entityType='company'
                    title={pageTitle}
                    id={operation !== Operation.Create ? companyId : undefined}
                    icon={<BusinessIcon />}
                    isCreate={operation === Operation.Create}
                    loading={isLoading && !data}
                    onCancel={onSuccess}
                    statusBadge={
                        draftState.Inactive ? (
                            <Chip
                                size='small'
                                label='Archived'
                                sx={{
                                    backgroundColor: colors.warning.light,
                                    color: colors.warning.dark,
                                    fontWeight: 600,
                                }}
                            />
                        ) : undefined
                    }
                    onSave={
                        canSave
                            ? () => {
                                if (reassigningCustomer) {
                                    setConfirmOpen(true);
                                    return;
                                }
                                handleSubmit();
                            }
                            : undefined
                    }
                    saveDisabled={!isValid}
                    saving={mutationLoading}
                    actions={
                        operation !== Operation.Create && companyId ? (
                            <Stack direction='row' spacing={1}>
                                <Button
                                    variant='secondary'
                                    onClick={() => setArchiveDialogOpen(true)}
                                    startIcon={
                                        draftState.Inactive ? <UnarchiveOutlinedIcon /> : <ArchiveOutlinedIcon />
                                    }>
                                    {draftState.Inactive ? 'Restore' : 'Archive'}
                                </Button>
                                <Button variant='secondary' onClick={() => navigateToActivity('company', companyId)}>
                                    View Activity
                                </Button>
                            </Stack>
                        ) : undefined
                    }
                />

                {/* Company Info Card */}
                <InfoCard title='Company Information' icon={<InfoOutlinedIcon />}>
                    <Grid container spacing={2}>
                        {operation !== Operation.Create && (
                            <ReadOnlyField label='Company ID' value={data?.CompanyID} size={6} mono />
                        )}

                        <DraftTextField
                            label='Company Name'
                            stateKey='CompanyName'
                            draft={draft}
                            disabled={operation === Operation.View}
                            validateFn={Validate.name}
                            helperText={{
                                valid: '',
                                invalid: 'Please provide a valid company name',
                            }}
                            size={operation !== Operation.Create ? 6 : 12}
                            required
                        />

                        {role.isAdmin && (
                            <DraftSelectField
                                label='Customer Name (Business Central)'
                                stateKey='CustomerID'
                                draft={draft}
                                disabled={operation !== Operation.Create && !role.prices.edit}
                                options={customerOptions}
                                size={12}
                                required
                                defaultFirst={false}
                                placeholder='Select a customer...'
                            />
                        )}

                        {operation !== Operation.View && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    sx={{ userSelect: 'none', ml: 0 }}
                                    checked={Boolean(draftState?.SalesRepTrackingEnabled)}
                                    onChange={({ target }) => {
                                        const checked = (target as HTMLInputElement).checked;
                                        setDraftState((prev) => ({
                                            ...prev,
                                            SalesRepTrackingEnabled: checked ? true : null,
                                        }));
                                    }}
                                    control={
                                        <Checkbox
                                            sx={{
                                                color: colors.neutral[400],
                                                '&.Mui-checked': {
                                                    color: colors.primary[500],
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            variant='body2'
                                            sx={{ color: colors.neutral[700], fontWeight: 500 }}>
                                            Sales Rep Tracking Enabled
                                        </Typography>
                                    }
                                />
                            </Grid>
                        )}

                        {operation !== Operation.View && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    sx={{ userSelect: 'none', ml: 0 }}
                                    // Default on: legacy null rows render as enabled; only explicit false unchecks
                                    checked={draftState?.DeviceIdEnabled !== false}
                                    onChange={({ target }) => {
                                        const checked = (target as HTMLInputElement).checked;
                                        setDraftState((prev) => ({ ...prev, DeviceIdEnabled: checked }));
                                    }}
                                    control={
                                        <Checkbox
                                            sx={{
                                                color: colors.neutral[400],
                                                '&.Mui-checked': {
                                                    color: colors.primary[500],
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            variant='body2'
                                            sx={{ color: colors.neutral[700], fontWeight: 500 }}>
                                            Device ID Enabled
                                        </Typography>
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                </InfoCard>

                {operation !== Operation.Create && companyId && (
                    <BannerEditor
                        scope={BannerScope.Company}
                        companyId={+companyId}
                        canEdit={canSave}
                        title='Company banners'
                        description='These banners override the global default for kiosks under this company. Leave empty to fall back to the global set.'
                    />
                )}

                {/* Audit Log */}
                {auditPk && operation !== Operation.Create && role.isAdmin && auditData.length > 0 && (
                    <ActivityCard audits={auditData} entityPk={auditPk} maxItems={5} />
                )}
            </EntityPageLayout>

            <ConfirmDialog
                open={archiveDialogOpen}
                onClose={() => setArchiveDialogOpen(false)}
                onConfirm={handleArchiveToggle}
                title={draftState.Inactive ? 'Restore Company' : 'Archive Company'}
                message={
                    draftState.Inactive
                        ? `Are you sure you want to restore "${draftState.CompanyName}"? This company will become visible in the active companies list.`
                        : `Are you sure you want to archive "${draftState.CompanyName}"? Archived companies are hidden from the main list but can be restored later.`
                }
                confirmText={draftState.Inactive ? 'Restore' : 'Archive'}
                variant={draftState.Inactive ? 'success' : 'warning'}
                loading={mutationLoading}
            />
            {/* Confirmation Modal */}
            <ConfirmModal
                title='Company Correlation'
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={handleSubmit}>
                <Typography>Are you sure you want to associate the following companies?</Typography>

                <List>
                    <ListItem>
                        <ListItemText
                            primary={`Company #${data?.CompanyID} - ${data?.CompanyName}`}
                            secondary='RapidCut'
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText
                            primary={`Company #${draftState?.CustomerID} - ${customerMap?.[draftState?.CustomerID as string]
                                }`}
                            secondary='Business Central'
                        />
                    </ListItem>
                </List>
            </ConfirmModal>
        </>
    );
};

export default CompanyPanel;
